import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Button, Modal, Notification, NotificationProps, Spinner, Text } from "@telia/teddy";
import { OrderValidationResult, ValidationError } from "../../../views/Dashboard/useOrderValidation";
import { reportValidationErrorsToGa } from "./reportValidationErrorsToGa";
import MissingTeliaSkyEmailModal from "../../../views/Dashboard/MissingTeliaSkyEmailModal";

const ValidationMessage: React.FC<{
    variant: NotificationProps["Root"]["variant"];
    text: ReactNode;
    heading?: string;
}> = ({ variant, text, heading }) => {
    return (
        <Notification type="tile" variant={variant}>
            <Notification.Icon />
            {heading && <Notification.Heading>{heading}</Notification.Heading>}
            <Notification.Text>{text}</Notification.Text>
        </Notification>
    );
};

interface ValidationMessagesProps {
    isValidating: boolean;
    isValidationError: boolean;
    refetchOrderValidation: () => void;
    orderValidation: OrderValidationResult | undefined;
}

const ValidationMessages: React.FC<ValidationMessagesProps> = ({
    isValidating,
    isValidationError,
    refetchOrderValidation,
    orderValidation,
}) => {
    const lastReportedErrorKey = useRef<string | null>(null);
    const [validationErrorModalOpen, setValidationErrorModalOpen] = useState(false);
    const [missingTeliaSkyEmailModalOpen, setMissingTeliaSkyEmailModalOpen] = useState(false);

    const renderEmptyErrorsText = (errors: ValidationError[] | undefined) => {
        if (errors && errors.length > 0) {
            return (
                <>
                    Ordrevalidering feilet.{" "}
                    <Button variant="text" onClick={() => setValidationErrorModalOpen(true)}>
                        Les mer
                    </Button>
                </>
            );
        }
        return "Ordrevalidering feilet. Kontroller ordredetaljene og prøv igjen.";
    };

    useEffect(() => {
        if (orderValidation?.orderState === "VALIDATION_FAILED") {
            const errorKey = orderValidation.errors.map((e) => e.message).join("\x00");
            if (errorKey !== lastReportedErrorKey.current) {
                lastReportedErrorKey.current = errorKey;
                reportValidationErrorsToGa(orderValidation.errors.map((e) => e.message));
            }
        } else {
            lastReportedErrorKey.current = null;
        }
    }, [orderValidation?.orderState, orderValidation?.errors]);

    const translatedErrors = orderValidation?.errors.filter((e) => e.isTranslated) ?? [];

    const renderTranslatedErrorsWithActions = () => (
        <>
            {translatedErrors.map((error, idx) => (
                <React.Fragment key={idx}>
                    <span dangerouslySetInnerHTML={{ __html: error.message }} />
                    {error.errorCode === "TELIA_SKY_MISSING_EMAIL_ADDRESS" && (
                        <>
                            {" "}
                            <Button ml="150" variant="text" onClick={() => setMissingTeliaSkyEmailModalOpen(true)}>
                                Legg til e-post
                            </Button>
                        </>
                    )}
                    <br />
                </React.Fragment>
            ))}
        </>
    );

    return (
        <>
            {isValidating && (
                <Notification type="tile" variant="information">
                    <Notification.Icon>
                        <Spinner size="sm" />
                    </Notification.Icon>
                    <Notification.Text>Validerer ordre...</Notification.Text>
                </Notification>
            )}
            {!isValidating && isValidationError && (
                <ValidationMessage
                    variant="error"
                    heading="Kunne ikke validere ordren"
                    text={
                        <>
                            Noe gikk galt under validering.{" "}
                            <Button variant="text" onClick={() => refetchOrderValidation()}>
                                Prøv igjen
                            </Button>
                        </>
                    }
                />
            )}
            {!isValidating &&
                orderValidation?.orderState &&
                (() => {
                    const state = orderValidation.orderState;
                    if (state === "COMPLETED" || state === "FINALIZED") {
                        return (
                            <ValidationMessage
                                variant="success"
                                text={`Ordren er fullført (status: ${state.toLowerCase()}).`}
                            />
                        );
                    }
                    if (state === "PROCESSING" || state === "SUBMITTING" || state === "VALIDATING") {
                        return (
                            <ValidationMessage
                                variant="information"
                                text={`Ordren er under behandling (status: ${state.toLowerCase()}).`}
                            />
                        );
                    }
                    if (state === "VALIDATION_FAILED") {
                        return (
                            <ValidationMessage
                                variant="warning"
                                text={
                                    translatedErrors.length > 0
                                        ? renderTranslatedErrorsWithActions()
                                        : renderEmptyErrorsText(orderValidation.errors)
                                }
                            />
                        );
                    }
                    if (state === "SUBMITTING_FAILED") {
                        return <ValidationMessage variant="warning" text="Innsending av ordren feilet. Prøv igjen." />;
                    }
                    return null;
                })()}
            {!isValidating && orderValidation?.validationPassed === true && orderValidation.orderState && (
                <ValidationMessage variant="success" text="Ordren er validert og klar til signering." />
            )}
            {!isValidating &&
                orderValidation &&
                orderValidation.validationPassed === false &&
                orderValidation.orderState !== "VALIDATION_FAILED" && (
                    <ValidationMessage
                        variant="warning"
                        text={
                            translatedErrors.length > 0
                                ? renderTranslatedErrorsWithActions()
                                : renderEmptyErrorsText(orderValidation.errors)
                        }
                    />
                )}
            <MissingTeliaSkyEmailModal
                isOpen={missingTeliaSkyEmailModalOpen}
                onClose={() => setMissingTeliaSkyEmailModalOpen(false)}
            />
            {validationErrorModalOpen && orderValidation?.errors && orderValidation.errors.length > 0 && (
                <Modal open onOpenChange={(open) => setValidationErrorModalOpen(open)}>
                    <Modal.Overlay>
                        <Modal.Content>
                            <Modal.Group variant="title">
                                <Modal.Title as="h3">Valideringsfeil</Modal.Title>
                                <Modal.Close
                                    aria-label="lukk"
                                    slot="floating"
                                    onClick={() => setValidationErrorModalOpen(false)}
                                />
                            </Modal.Group>
                            {orderValidation.errors.map((error, idx) => (
                                <Notification key={idx} type="tile" variant="warning" mt="100">
                                    <Notification.Icon />
                                    <Notification.Text>
                                        <span dangerouslySetInnerHTML={{ __html: error.message }} />
                                    </Notification.Text>
                                </Notification>
                            ))}
                            <Text mt="300">
                                Dette kan være en feil i Nexus og den har blitt automatisk rapportert inn til
                                Nexus-teamet. Om feilen vedvarer, må Salsa brukes inntil videre.
                            </Text>
                        </Modal.Content>
                    </Modal.Overlay>
                </Modal>
            )}
        </>
    );
};

export default ValidationMessages;
